// Detailed Profile Progress Component for Profile Page
// Shows expandable progress with all checklist items

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Shield, CheckCircle, Circle, ChevronDown, ChevronUp, 
  User, Building, FileText, Video, Mail, Phone, 
  Globe, Package, ArrowRight, Loader2, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Map progress item keys to icons
const getItemIcon = (key) => {
  const iconMap = {
    full_name: User,
    email: Mail,
    email_verified: Mail,
    phone: Phone,
    phone_verified: Phone,
    company_name: Building,
    business_id: FileText,
    business_id_verified: FileText,
    gstin: FileText,
    gstin_verified: FileText,
    iec_code: FileText,
    video_verification: Video,
    video_verified: Video,
    products: Package,
    profile_complete: User,
    country: Globe
  };
  return iconMap[key] || Circle;
};

export default function DetailedProfileProgress({ userId, userRole = 'seller' }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchProgress();
    }
  }, [userId, userRole]);

  const fetchProgress = async () => {
    try {
      setError(null);
      const endpoint = userRole === 'seller' 
        ? `${API}/api/profile/progress/seller/${userId}`
        : `${API}/api/profile/progress/buyer/${userId}`;
      
      const response = await axios.get(endpoint);
      
      // Handle the nested response structure
      if (response.data.progress) {
        setProgress(response.data.progress);
      } else {
        setProgress(response.data);
      }
    } catch (err) {
      console.error('Progress fetch error:', err);
      setError('Could not load progress details');
      // Set default progress on error
      setProgress({ 
        overall_percentage: 0, 
        next_priority: 'Complete your profile',
        missing_items: [],
        completed_items: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (action) => {
    // Navigate to appropriate section based on action
    const actionRoutes = {
      'Add email': '/profile?section=email',
      'Verify email': '/profile?section=email',
      'Add phone': '/profile?section=phone',
      'Verify phone': '/profile?section=phone',
      'Add GSTIN': '/profile?section=gstin',
      'Verify GSTIN': '/profile?section=gstin',
      'Add video': '/dashboard?action=video',
      'Complete video verification': '/dashboard?action=video',
      'Add products': '/products'
    };

    const route = actionRoutes[action] || '/profile';
    navigate(route);
  };

  if (loading) {
    return (
      <Card data-testid="detailed-progress-loading">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Profile Completion</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-200 rounded w-full"></div>
            <div className="h-3 bg-slate-200 rounded w-3/4"></div>
            <div className="h-3 bg-slate-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!progress) return null;

  const percentage = progress.overall_percentage || 0;
  const isComplete = percentage >= 80;
  
  // Handle both array of strings and array of objects for completed/missing items
  const normalizeItem = (item, index) => {
    if (typeof item === 'string') {
      return {
        label: item,
        key: item.toLowerCase().replace(/\s+/g, '_'),
        weight: 10
      };
    }
    return item;
  };
  
  const completedItems = (progress.completed_items || []).map(normalizeItem);
  const missingItems = (progress.missing_items || []).map(normalizeItem);

  // Combine all items for display
  const allItems = [
    ...completedItems.map(item => ({ ...item, completed: true })),
    ...missingItems.map(item => ({ ...item, completed: false }))
  ];

  return (
    <Card className={`border-2 ${isComplete ? 'border-green-200 bg-green-50/30' : 'border-blue-200 bg-blue-50/30'}`} data-testid="detailed-profile-progress">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50/50 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${isComplete ? 'bg-green-100' : 'bg-blue-100'}`}>
                  <Shield className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-blue-600'}`} />
                </div>
                <div className="text-left">
                  <CardTitle className="text-lg">
                    {userRole === 'buyer' ? 'Buyer Profile' : 'Seller Profile'} Completion
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {isComplete ? 'Your profile is complete!' : 'Complete your profile to build trust'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`text-2xl font-bold ${isComplete ? 'text-green-600' : 'text-blue-600'}`}>
                  {percentage}%
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </div>
            <Progress value={percentage} className="h-2 mt-3" />
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {error && (
              <div className="flex items-center gap-2 text-amber-600 mb-4 text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Completed Items */}
            {completedItems.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Completed ({completedItems.length})
                </h4>
                <div className="space-y-2">
                  {completedItems.map((item, index) => {
                    const ItemIcon = getItemIcon(item.key);
                    return (
                      <div 
                        key={index} 
                        className="flex items-center gap-3 p-2 bg-green-50 rounded-lg border border-green-100"
                      >
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <ItemIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800">{item.label}</span>
                        <Badge variant="outline" className="ml-auto text-green-600 border-green-300 text-xs">
                          +{item.weight || 10}%
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Missing Items */}
            {missingItems.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Circle className="h-4 w-4" />
                  To Complete ({missingItems.length})
                </h4>
                <div className="space-y-2">
                  {missingItems.map((item, index) => {
                    const ItemIcon = getItemIcon(item.key);
                    const isPriority = index === 0;
                    return (
                      <div 
                        key={index} 
                        className={`flex items-center gap-3 p-2 rounded-lg border ${
                          isPriority ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <Circle className={`h-4 w-4 flex-shrink-0 ${isPriority ? 'text-blue-400' : 'text-slate-300'}`} />
                        <ItemIcon className={`h-4 w-4 flex-shrink-0 ${isPriority ? 'text-blue-600' : 'text-slate-500'}`} />
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm ${isPriority ? 'text-blue-800 font-medium' : 'text-slate-700'}`}>
                            {item.label}
                          </span>
                          {isPriority && (
                            <Badge className="ml-2 bg-blue-600 text-xs">Next</Badge>
                          )}
                        </div>
                        <Badge variant="outline" className="text-slate-500 border-slate-300 text-xs flex-shrink-0">
                          +{item.weight || 10}%
                        </Badge>
                        {item.action && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleActionClick(item.action);
                            }}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Next Priority CTA */}
            {progress.next_priority && !isComplete && (
              <div className="mt-4 p-3 bg-blue-100 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Recommended Next Step</p>
                    <p className="text-sm text-blue-700">{progress.next_priority}</p>
                  </div>
                  <Button 
                    size="sm" 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleActionClick(progress.next_priority)}
                  >
                    Complete Now
                  </Button>
                </div>
              </div>
            )}

            {/* Complete state */}
            {isComplete && (
              <div className="mt-4 p-3 bg-green-100 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Profile Complete!</p>
                    <p className="text-sm text-green-700">
                      Your profile is verified and visible to {userRole === 'buyer' ? 'sellers' : 'buyers'}.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
